import { useCallback, useMemo, useRef } from 'react';

import {
  btag,
  constants,
  NotificationManager,
  PaymentV2Api,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
} from '@atlas/core';
import { onDepositSubmit } from '@atlas/react-forms';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';

import { ACCOUNT_NUMBERS_POPUP_TYPES, DATA_LAYER_STORAGE_KEYS } from 'common/constants';
import {
  buildPaymentUrl,
  showAlternativeMethodsOrError,
} from 'src/common/Popup/alternativePaymentUtils';
import { useBonusesContext } from 'src/components/BonusesProvider';
import { getMinDeposit, isAmountBelowMinDeposit } from 'src/components/BonusesProvider/utils';
import { depositSubmit } from 'src/helpers/depositSubmit';

const hasMultipleBankAccounts = bankAccounts =>
  Array.isArray(bankAccounts) ? bankAccounts.length > 1 : bankAccounts?.data?.length > 1;

export const useDepositSubmit = ({
  account,
  backUrl,
  bankAccounts,
  browser,
  currency,
  ip,
  isAccountBlocked,
  isCashboxDepositClicked,
  locale,
  onDepositLoaderShown,
  onDepositRedirectSuccess,
  onDepositStart,
  onDepositSuccess,
  os,
  paymentMethod,
  push,
  ref,
  setPaymentUrl,
  setRedirectUrl,
  showSelfRegulationPopup,
  showSelfRegulationPopupIfNeeded,
  showSpentLimitPopup,
  showSpentLimitPopupIfNeeded,
  wallet,
}) => {
  const loadingNotificationId = useRef();
  const loaderShownAt = useRef();
  const { depositAmount, selectedBonus } = useBonusesContext();

  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const depositPageUrl = `${clientProfilePath}/${clientProfileRoutes.payment}/${paymentRoutes.deposit}`;
  const isPreselectedPaymentMethod = !!SiteConfigManager.getConfig(
    'config.config.mobile.preselected_payment_method',
  );

  const onCreateDeposit = useCallback(() => {
    ref.current?.setIsLoading?.(false);
    loadingNotificationId.current = NotificationManager.display({
      data: {
        amount: depositAmount,
        pmImage: paymentMethod?.desktopImageLink,
        pmName: paymentMethod?.name,
        text: 'preparing_deposit_text',
        title: 'preparing_deposit_title',
      },
      type: 'depositLoading',
    });
    loaderShownAt.current = Date.now();

    onDepositLoaderShown?.({
      amount: depositAmount,
      currency,
      locale,
      method: paymentMethod?.name,
    });
  }, [ref, depositAmount, paymentMethod, currency, locale, onDepositLoaderShown]);

  const onDepositStatusError = useCallback(
    error => {
      NotificationManager.removeNotification(loadingNotificationId.current);

      const fallbackError = () => {
        NotificationManager.display({
          data: {
            className: 'deposit-failed-popup',
            headerTitle: 'deposit',
            text: error?.code ? error.code.toLowerCase() : 'deposit_failed_message',
            title: 'deposit_failed_title',
          },
          type: 'errorTransaction',
        });
      };

      if (!paymentMethod?.alternativePmEnabled) {
        fallbackError();
        return;
      }

      showAlternativeMethodsOrError({
        fallbackError,
        onSelectMethod: method => {
          const baseUrl = isPreselectedPaymentMethod ? depositPageUrl : backUrl;
          push(buildPaymentUrl(`${baseUrl}/${method.id}`, depositAmount, method));
        },
        onViewAll: () => {
          if (isPreselectedPaymentMethod) {
            push(buildPaymentUrl(depositPageUrl, depositAmount, null), {
              showAllMethods: true,
            });
            return;
          }

          push(buildPaymentUrl(backUrl, depositAmount, null), {
            showAllMethods: true,
          });
        },
        operationType: 'DEPOSIT',
        pmId: paymentMethod?.id,
      });
    },
    [backUrl, depositPageUrl, depositAmount, isPreselectedPaymentMethod, paymentMethod, push],
  );

  const submitHandler = useCallback(
    data => {
      depositSubmit(
        data,
        paymentMethod,
        setPaymentUrl,
        depositAmount,
        setRedirectUrl,
        data?.paymentOptions,
      );
    },
    [paymentMethod, setPaymentUrl, depositAmount, setRedirectUrl],
  );

  const performPaymentRequest = useCallback(
    async paymentOptions => {
      const shouldSendBonus =
        selectedBonus &&
        !selectedBonus.disabled &&
        !isAmountBelowMinDeposit(paymentOptions.amount, getMinDeposit(selectedBonus, currency));
      const bonusInstanceId = selectedBonus?.id;
      const bonusId = selectedBonus?.bonusId;

      onDepositStart?.({
        amount: paymentOptions.amount,
        bonus_id: bonusId,
        payment_method: paymentMethod?.name ?? null,
      });

      const paymentOptionsWithCurrency = {
        ...paymentOptions,
        currency,
        ...(shouldSendBonus && {
          additionalAttributes: {
            bonusId,
            bonusInstanceId,
          },
        }),
      };

      const res = await PaymentV2Api.getPaymentData({
        onCreatePayment: onCreateDeposit,
        onPaymentStatusError: onDepositStatusError,
        paymentOptions: paymentOptionsWithCurrency,
        paymentType: PaymentV2Api.PaymentType.DEPOSIT,
      });

      NotificationManager.removeNotification(loadingNotificationId.current);

      onDepositRedirectSuccess?.({
        amount: paymentOptions.amount,
        currency,
        locale,
        method: paymentMethod?.name,
        t_wait_ms: loaderShownAt.current ? Date.now() - loaderShownAt.current : null,
      });

      pushToDataLayer(
        'deposit_created',
        {
          amount: paymentOptions.amount,
          client: { id: account.id },
          created_at: Date.now(),
          currency: wallet.currency,
          payment_method: res.paymentName,
        },
        btag,
      );

      onDepositSuccess?.({
        transaction_id: res?.publicPaymentId ?? res?.paymentId ?? null,
      });

      return {
        ...res,
        paymentOptions: paymentOptionsWithCurrency,
      };
    },
    [
      account.id,
      currency,
      locale,
      onDepositStart,
      onDepositSuccess,
      onCreateDeposit,
      onDepositRedirectSuccess,
      onDepositStatusError,
      paymentMethod?.name,
      selectedBonus,
      wallet.currency,
    ],
  );

  const checkLimits = useCallback(async () => {
    if (isAccountBlocked) {
      NotificationManager.display({ id: 'accountBlocked', type: 'accountBlocked' });
      return false;
    }
    if (showSpentLimitPopup) {
      await showSpentLimitPopupIfNeeded();
      return false;
    }
    if (showSelfRegulationPopup) {
      await showSelfRegulationPopupIfNeeded();
      return false;
    }
    return true;
  }, [
    isAccountBlocked,
    showSpentLimitPopup,
    showSelfRegulationPopup,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
  ]);

  const action = useMemo(() => {
    if (paymentMethod?.displayAccountNumberSelector && hasMultipleBankAccounts(bankAccounts)) {
      return async formData => {
        if (!(await checkLimits())) return;
        NotificationManager.display({
          data: {
            amount: formData.amount,
            onDepositStatusError,
            paymentAction: performPaymentRequest,
            paymentMethod,
            paymentSubmit: submitHandler,
            pmId: formData.pmId,
            type: ACCOUNT_NUMBERS_POPUP_TYPES.DEPOSIT,
          },
          message: 'accountNumbers',
          type: 'accountNumbers',
        });
      };
    }

    return onDepositSubmit(async paymentOptions => {
      if (!(await checkLimits())) return;
      ref.current?.setIsLoading?.(true);
      const res = await performPaymentRequest(paymentOptions);

      if (!isCashboxDepositClicked) {
        pushToDataLayer(
          'cashbox_deposit_click',
          {
            OS: os,
            amount: paymentOptions.amount,
            browser,
            created_at: Date.now(),
            device: constants.DEVICE_TYPES.MOBILE,
            ip,
            locale,
            page_referer: window.location.pathname,
            payment_method: paymentMethod?.name,
            user_id: account.id,
          },
          btag,
        );
        SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.CASHBOX_DEPOSIT_CLICKED, true);
      }
      return res;
    });
  }, [
    account.id,
    browser,
    bankAccounts,
    checkLimits,
    ip,
    isCashboxDepositClicked,
    locale,
    os,
    paymentMethod,
    performPaymentRequest,
    ref,
    submitHandler,
    onDepositStatusError,
  ]);

  return { action, submitHandler };
};
